import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
import { qk } from '../lib/queryClient.ts';
import { navigate } from '../lib/router.ts';
import { SkeletonCardGrid } from '../components/ui/Skeleton.tsx';

const KIND_LABEL: Record<string, string> = {
  public: '공개',
  sponsored: '스폰서',
  private: '비공개',
};

export function BrowseCollectionsPage() {
  const queryClient = useQueryClient();
  const q = useQuery({
    queryKey: ['browse-collections'],
    queryFn: () => api.browseCollections(1, 48),
  });
  const meQ = useQuery({ queryKey: qk.auth.me(), queryFn: () => api.me(), retry: false });
  const isLoggedIn = !!meQ.data?.user;
  const myRowsQ = useQuery({
    queryKey: qk.myLandingRows(),
    queryFn: () => api.myLandingRows(),
    enabled: isLoggedIn,
  });

  // code → rowId (내 랜딩에 등록된 컬렉션).
  const registered = new Map<string, string>();
  for (const r of myRowsQ.data?.rows ?? []) {
    if (r.kind === 'collection' && r.code) registered.set(r.code, r.rowId);
  }

  const refresh = async () => {
    await queryClient.invalidateQueries({ queryKey: qk.myLandingRows() });
    await queryClient.invalidateQueries({ queryKey: qk.landing() });
  };

  const addMut = useMutation({
    mutationFn: (code: string) => api.addLandingRow(code),
    onSuccess: refresh,
  });
  const removeMut = useMutation({
    mutationFn: (rowId: string) => api.removeLandingRow(rowId),
    onSuccess: refresh,
  });

  const items = q.data?.items ?? [];
  const busy = addMut.isPending || removeMut.isPending;

  return (
    <main className="nf-page">
      <div className="nf-fav-head">
        <div>
          <h1>컬렉션 둘러보기</h1>
          <div className="sub">사람들이 직접 큐레이션한 서비스 모음.</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="nf-btn"
            style={{ height: 36, padding: '0 14px', fontSize: 13 }}
            onClick={() => navigate('/favorites/collections')}
          >
            내 컬렉션
          </button>
        </div>
      </div>

      <div style={{ padding: '0 56px 80px' }}>
        {q.isPending ? (
          <SkeletonCardGrid count={8} />
        ) : items.length === 0 ? (
          <div style={{ color: 'var(--fg-muted)', textAlign: 'center', padding: '60px 0' }}>
            아직 공개된 컬렉션이 없어요.
          </div>
        ) : (
          <div className="nf-coll-grid">
            {items.map((c) => {
              const rowId = registered.get(c.publicCode);
              const isReg = !!rowId;
              return (
                <div
                  key={c.id}
                  className="nf-coll-browse-card"
                  onClick={() => navigate('/col/' + c.publicCode)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <span style={{ fontWeight: 800, fontSize: 17, letterSpacing: '-.02em' }}>{c.title}</span>
                    <span className={`nf-coll-badge ${c.kind}`}>{KIND_LABEL[c.kind]}</span>
                  </div>
                  {c.description && (
                    <p style={{ fontSize: 13, color: 'var(--fg-soft)', lineHeight: 1.5, margin: '0 0 14px' }}>
                      {c.description}
                    </p>
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--fg-muted)' }}>
                    {c.owner.avatar ? (
                      <img src={c.owner.avatar} alt="" width={20} height={20} style={{ borderRadius: '50%', objectFit: 'cover' }} />
                    ) : null}
                    <span>{c.owner.displayName}</span>
                    <span>·</span>
                    <span>서비스 {c.serviceCount}개</span>
                  </div>
                  {isLoggedIn && (
                    <button
                      type="button"
                      className={`nf-btn ${isReg ? '' : 'primary'}`}
                      disabled={busy}
                      style={{ marginTop: 12, width: '100%' }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isReg) removeMut.mutate(rowId!);
                        else addMut.mutate(c.publicCode);
                      }}
                    >
                      {isReg ? '내 랜딩에서 빼기' : '내 랜딩에 추가'}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
}
